import React, { FC, useContext, useEffect, useState } from 'react'
import { TRANSLATIONS as T } from '../../../constants/translations-constants'
import { CommonPageProps } from '../../../types/interface/fsm.interface'
import Loading from '../../qb/Loading/Page'
import { AvtalegiroForm, LDContainerV2FSM, LMBasicV3FSM, Typography, withHidden } from '@dg-shared'
import { useTranslate } from '../../../hooks/shared'
import useReactQueryPolicy from '../../../hooks/shared/useReactQueryPolicy'
import useAvtalegiro from '../../../hooks/shared/useAvtalegiro'
import {
  filterVehiclesWithoutPolicies,
  getProductName,
  hasPolicyCoverageRemaining,
  normalizeMotorRegistrationNumber,
} from '@dg-util'
import FNOLMyPagesDEVButton from '@dg-shared/FNOLMyPagesDEVButton'
import { FlowName } from '../../../config'
import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'
import PoliciesListCards from '../../../components/shared/PoliciesListCards'
import { PolicyV2Type, VehicleV2Type } from '../../../types/ApiV2'
import { FSMCommonTransitions, Events } from '../../../FSM/shared/constants'
import useReactQueryPerson from '../../../hooks/shared/useReactQueryPerson'
import { ProductName, PartnerName } from '../../../types/interface/quote.interface'
import { Context } from '@dg-shared/ContextProvider'
import {
  ORIGINAL_POLICY,
  QUOTE_ID,
  SELECTED_POLICY_NUMBER,
  SELECTED_POLICY_VERSION,
  SELECTED_QUOTE_MOTOR_REGISTRATION_NUMBER,
} from '../../../constants'
import ProductSelect from '@dg-shared/ProductSelect'
import { Grid } from '@mui/material'
import OfferItem from '../../qb/Offers/OfferItem'
import { switchFSM } from '@dg-util/productHandler'
import { FSMService } from '../../../FSM'
import NoPolicies from '@dg-shared/NoPolicies'

const MobileScreen = withHidden(LMBasicV3FSM, true)
const DesktopScreen = withHidden(LDContainerV2FSM, false)

export const useStyles = makeStyles()((theme: Theme) => ({
  title: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },

  cardItem: {
    minHeight: theme.spacing(18),
  },

  mobileContentWrapper: {
    justifyContent: 'flex-start !important',
  },
}))

const PageList: FC<CommonPageProps> = ({ service }) => {
  const { t } = useTranslate(T.POLICIES)
  const { classes } = useStyles()
  const { policies, isLoading } = useReactQueryPolicy()
  const { personAllVehicles, isLoading: isPersonVehiclesLoading } = useReactQueryPerson()
  const vehicles = personAllVehicles
  const vehiclesWithoutPolicies = filterVehiclesWithoutPolicies(vehicles, policies)
  const { setContext } = useContext(Context)
  const [productFilter, setProductFilter] = useState<ProductName>(getProductName())

  // Avtalegiro setup
  const {
    avtalegiroFormRef,
    KID,
    returnUrl,
    actionUrl,
    companyName,
    companyAccountNumber,
    handleCreateAvtalegiro,
  } = useAvtalegiro()

  useEffect(() => {
    // Remove the current policy number, version, quoteId and registration number to avoid the wrong version fetching (Fetch current active policy without version)
    setContext({
      [SELECTED_POLICY_VERSION]: null,
      [SELECTED_POLICY_NUMBER]: null,
      [SELECTED_QUOTE_MOTOR_REGISTRATION_NUMBER]: null,
      [QUOTE_ID]: null,
      [ORIGINAL_POLICY]: null,
    })
  }, [setContext])

  const title = t('title')
  const backBtnText = t('back', {}, 'string')
  const subtitle = policies?.length > 1 && t('subtitle')

  const filteredPolicies = policies
    .filter((policy: PolicyV2Type) => policy.productName === productFilter)
    // Keep a policy whose last day of cover is TODAY. Comparing `InsuredTo` against the
    // end of today dropped it, and an endorsement puts every changed policy in exactly
    // that state until midnight — which is how a completed change "lost" the policy
    // (DFE-4112).
    .filter((policy: PolicyV2Type) => hasPolicyCoverageRemaining(policy))

  const additionalOffersPerProduct: Record<ProductName, React.ReactNode> = {
    [ProductName.VEHICLE]: vehiclesWithoutPolicies.map((vehicle: VehicleV2Type) => (
      <Grid key={vehicle.MotorRegistrationNumber} item xs={12} md={4}>
        <OfferItem
          customClassName={classes.cardItem}
          vehicle={vehicle}
          translations={{
            vehicleCardTitle: t('vehicleCardTitle'),
          }}
          showImage={false}
          handleClick={() => {
            const selectedValues = {
              [SELECTED_QUOTE_MOTOR_REGISTRATION_NUMBER]: normalizeMotorRegistrationNumber(
                vehicle.MotorRegistrationNumber
              ),
            }

            setContext(selectedValues)

            // Switch FSM to the selected product name QB flow Ownership state
            switchFSM({
              targetStateValue: FSMCommonTransitions.QB_OWNERSHIP,
              targetFlow: FlowName.QUOTE_AND_BUY,
              targetProduct: productFilter,
              FSMService: FSMService,
            })
          }}
        />
      </Grid>
    )),
    [ProductName.TRAVEL]: null,
    [ProductName.CONTENT]: null,
  }

  const additionalOffers = additionalOffersPerProduct[productFilter]

  const body = (
    <>
      <ProductSelect
        handleProductChange={(product: ProductName) => {
          setProductFilter(product)

          // Switch FSM to the selected product name
          switchFSM({
            targetStateValue: FSMCommonTransitions.POLICIES,
            targetFlow: FlowName.MY_PAGES,
            targetProduct: product,
            FSMService: FSMService,
          })
        }}
        selectedProduct={productFilter}
      />
      <NoPolicies policies={filteredPolicies} additionalOffers={additionalOffers} />
      <AvtalegiroForm
        actionUrl={actionUrl}
        componentRef={avtalegiroFormRef}
        companyName={companyName}
        companyAccountNumber={companyAccountNumber}
        data={{}}
        kid={KID}
        returnUrl={returnUrl}
        notificationDisabled='false'
      />
      <PoliciesListCards
        policies={filteredPolicies}
        service={service}
        additionalOffers={additionalOffers}
        onAvtaleGiroClick={handleCreateAvtalegiro}
        plusButtonHandler={
          PARTNER_NAME !== PartnerName.instabank
            ? () => {
                const targetStateValueMap: Record<ProductName, FSMCommonTransitions> = {
                  [ProductName.VEHICLE]: FSMCommonTransitions.QB_FIND,
                  [ProductName.CONTENT]: FSMCommonTransitions.QB_FIND,
                  [ProductName.TRAVEL]: FSMCommonTransitions.QB_REVIEW,
                }

                // Switch FSM to the selected product name and QB flow
                switchFSM({
                  targetStateValue: targetStateValueMap[productFilter],
                  targetFlow: FlowName.QUOTE_AND_BUY,
                  targetProduct: productFilter,
                  FSMService: FSMService,
                })
              }
            : undefined
        }
      />
    </>
  )

  if (isLoading || isPersonVehiclesLoading) {
    return <Loading />
  }

  return (
    <>
      <MobileScreen
        titleHeader={title}
        paragraphSubTitle={subtitle}
        breadcrumbs
        breadcrumbsBtnText={backBtnText}
        send={() => FSMService.flowService.send({ type: Events.BACK })}
        customClass={classes.mobileContentWrapper}
      >
        <FNOLMyPagesDEVButton />
        {body}
      </MobileScreen>
      <DesktopScreen
        breadcrumbs
        breadcrumbsBtnText={backBtnText}
        send={() => FSMService.flowService.send({ type: Events.BACK })}
      >
        <FNOLMyPagesDEVButton />
        <Typography type={'h1'} className={classes.title}>
          {title}
        </Typography>
        {subtitle && <Typography>{subtitle}</Typography>}
        {body}
      </DesktopScreen>
    </>
  )
}

export default PageList
